import { useAccount } from '@azure/msal-react';
import React, { createContext, useContext } from 'react';
import { UserAccessDetails, useGetUserRoles } from '../hooks/getRoles';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { ErrorPage } from '@im/ui-lib/ErrorPage';
import { useTranslate, useLocalizationContext } from '@im/react-localization';
import { IM_PRICING_GDE_UI_SECURITYGROUP_ENABLED } from 'src/util/constants';
import { useFeatureFlagEnabled } from 'src/hooks/useFeatureFlagEnabled';
import { useSearchParams } from 'react-router-dom';

const UserRoleContext = createContext<UserAccessDetails>({
  defaultCountryCompanyCode: '',
  defaultRoleId: 0,
  listOfAccessableCountries: [],
  role: [],
});
export default function UserRoleProvider(prop: any) {
  const translate = useTranslate();
  const { data, error, isError, isLoading } = useGetUserRoles({
    Usermailid: useAccount()?.username.toLocaleLowerCase() ?? '',
  });
  return (
    <>
      {isLoading && <CenterLoader />}
      {data && data?.defaultRoleId !== 0 && (
        <UserRoleContext.Provider value={data}>
          {prop.children}
        </UserRoleContext.Provider>
      )}
      {(isError || data?.defaultRoleId === 0) && !isLoading && (
        <ErrorPage
          errorCode={isError ? String(error?.status) : '403'}
          errorText={
            isError
              ? error?.message
              : translate('Common.AccessDeniedDescription')
          }
          message={isError ? undefined : translate('Common.AccessDeniedError')}
        />
      )}
    </>
  );
}
export const useUserRole = () => useContext(UserRoleContext);

UserRoleProvider.Consumer = UserRoleContext.Consumer;

export const useRoles = () => {
  const isSecurityGroupEnabled = useFeatureFlagEnabled(
    IM_PRICING_GDE_UI_SECURITYGROUP_ENABLED
  );

  const userRoleData = useUserRole();
  const [searchParams] = useSearchParams();
  const l10nContext = useLocalizationContext();
  const { locale } = l10nContext;

  const countryCode = searchParams.get('countryCode') ?? locale.split('-')[1];

  if (isSecurityGroupEnabled) {
    const userRole = userRoleData.listOfAccessableCountries;

    return {
      isCOGUser: userRole.some(
        (role) =>
          role.roleId === 110 && role.siteId === countryCode.toLowerCase()
      ),
      isConfiguratorAdminUser: userRole.some(
        (role) =>
          role.roleId === 210 && role.siteId === countryCode.toLowerCase()
      ),
      isConfiguratorDeployer: userRole.some(
        (role) =>
          role.roleId === 230 && role.siteId === countryCode.toLowerCase()
      ),
      isConfiguratorUser: userRole.some(
        (role) =>
          role.roleId === 200 && role.siteId === countryCode.toLowerCase()
      ),
      isConfiguratorViewer: userRole.some(
        (role) =>
          role.roleId === 220 && role.siteId === countryCode.toLowerCase()
      ),
      isSalesRole: userRole.some(
        (role) =>
          role.roleId === 100 && role.siteId === countryCode.toLowerCase()
      ),
      isSalesUser: userRole.some(
        (role) =>
          (role.roleId === 100 || role.roleId === 200 || role.roleId === 210) &&
          role.siteId === countryCode.toLowerCase()
      ),
      isSuperAdminUser: userRole.some(
        (role) =>
          role.roleId === 120 && role.siteId === countryCode.toLowerCase()
      ),
      isVBMUser: userRole.some(
        (role) =>
          role.roleId === 130 && role.siteId === countryCode.toLowerCase()
      ),
    };
  }

  const userRole = userRoleData.role;
  return {
    isCOGUser: userRole.includes(110),
    isQuoteConfigurator: userRole.includes(200),
    isQuoteConfiguratorReviewer: userRole.includes(210),
    isQuoteConfiguratorViewer: userRole.includes(100) || userRole.includes(110),
    isSalesRole: userRole.includes(100),
    isSalesUser:
      userRole.includes(100) ||
      userRole.includes(200) ||
      userRole.includes(210) ||
      userRole.includes(130),
    isSuperAdminUser: userRole.includes(120),
    isVBMUser: userRole.includes(130),
  };
};

export const useDisplayUserRoles = () => {
  const translate = useTranslate();
  return [
    { label: translate('UserRoles.UserRoleSales'), value: 100 },
    { label: translate('UserRoles.UserRoleFinance'), value: 110 },
    { label: translate('UserRoles.UserRoleVBM'), value: 130 },
    { label: translate('UserRoles.UserRoleQuoteConfigurator'), value: 200 },
    {
      label: translate('UserRoles.UserRoleQuoteConfiguratorDeployer'),
      value: 210,
    },
  ];
};
